/* List item ↔ domain conversions.
 *
 * Every field name here must match a column in
 * provisioning/Provision-LedgerSite.ps1. Nothing enforces that at compile
 * time and Graph will not complain — a renamed column comes back absent, so the
 * app renders a blank and looks like it worked. `npm run check:schema` is what
 * catches it; run it after editing either side.
 *
 * Conventions:
 *   toX(id, fields)  read  — always total, never throws on a missing field
 *   xFields(patch)   write — only includes keys present on the patch, so a
 *                            partial update cannot blank what it did not mention
 */

import type {
  Criticality,
  DepotId,
  DeviceType,
  FileKind,
  GroupType,
  IApplication,
  IArticle,
  IAssignment,
  IAttachment,
  IAuditEntry,
  IBackup,
  IChecklistRun,
  IChecklistTemplate,
  ICredential,
  ICustomRecord,
  IDevice,
  IDomain,
  IGroup,
  IGroupMember,
  ILicenceSku,
  ILocation,
  IModule,
  INetwork,
  IPerson,
  IRecordField,
  IRecordType,
  IRecoveryPlan,
  IRestoreTest,
  IRoleDefinition,
  ISpecialPermissions,
  ISsid,
  IStatusTile,
  ISubnet,
  ISupplier,
  IVpnTunnel,
  LedgerRole,
  PersonKind,
  PersonStatus,
  SignInState,
  RecordSource,
  Tone,
} from '../../models';
import type { ListItemFields } from './GraphClient';
import { bool, fromDepotId, num, parseJsonField, str, toDepotId, toIsoDate } from './lists';

/* ─────────────────────────── Small helpers ─────────────────────────── */

/** '1 — Critical' → 1. Choice columns carry the label; the domain wants the rank. */
function leadingDigit(value: unknown, fallback: number): number {
  const m = String(value ?? '').match(/^\s*(\d)/);
  return m ? Number(m[1]) : fallback;
}

/* A Choice column takes the label, not the number behind it.
 *
 * leadingDigit above reads "2 — Important" back as 2, which made it easy to
 * assume the reverse was String(2). It is not. SharePoint rejects a value
 * outside a Choice column's list and rejects the whole item with it, so writing
 * "1" did not save a wrong criticality — it failed the save, and the button
 * looked like it had done nothing.
 *
 * Matched character for character against Provision-LedgerSite.ps1. The dash
 * is an em dash; a hyphen here would fail in exactly the same way, and look
 * right while doing it. */
const CRITICALITY = ['1 — Critical', '2 — Important', '3 — Useful'] as const;
const TIER = ['1 — Routine', '2 — Serious', '3 — Major', '4 — Site loss'] as const;

/** The label whose leading digit is `n`. Null clears the column rather than
    writing something it will not take. */
function choiceOut(labels: readonly string[], n: unknown): string | null {
  return labels.find((l) => l.startsWith(String(n))) ?? null;
}

/** Exported for the test that checks these against the provisioning script. */
export const critOut = (n: unknown): string | null => choiceOut(CRITICALITY, n);
export const tierOut = (n: unknown): string | null => choiceOut(TIER, n);

/** A SharePoint boolean can arrive as null, meaning "not applicable" rather than
    false — the NAS repository record depends on that distinction. */
function triBool(value: unknown): boolean | null {
  if (value === true || value === false) return value;
  return null;
}

/** Display timestamp for the audit trail and backup runs. */
function toStamp(value: unknown): string {
  if (typeof value !== 'string' || !value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** A date on its way out to SharePoint.
 *
 *  An empty date column reads back as '' and a cleared one arrives from a form
 *  as '', and SharePoint rejects '' for a date with a 400 that fails the whole
 *  item — not just that field. It wants null. Every row the sync creates has
 *  blank dates, so the first edit of any of them hit this.
 *
 *  '—' is here because a few models use it for "no such date", and it is not a
 *  date either.
 */
function dateOut(value: string | undefined): string | null {
  const v = (value ?? '').trim();
  return v && v !== '—' ? v : null;
}

/** A number on its way out to SharePoint.
 *
 *  Exactly the dateOut problem, one column type along, and it took a save that
 *  would not go through to notice: a number input hands back '' when somebody
 *  clears it, and SharePoint answers '' on a Number column with a 400 that
 *  fails the whole item rather than that one field. So an emptied cost took the
 *  short name and the term end down with it, and the drawer reported that the
 *  licence could not be saved without saying why.
 *
 *  Anything that is not a number becomes null, which is what an empty number
 *  column already reads back as.
 */
function numberOut(value: unknown): number | null {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Lookup columns come back as '<id>' on the *LookupId field. */
function lookupId(value: unknown): string {
  const s = str(value);
  return s || '';
}

/* ─────────────────────────── People ─────────────────────────── */

export function toPerson(id: string, f: ListItemFields): IPerson {
  return {
    id,
    name: str(f.Title),
    upn: str(f.UPN),
    /* Both of these existed as columns and were written, but never read back —
       so Kind never survived a save and the sync's Email never reached a screen.
       check:schema did not catch it: it proves every field the app *reads* is
       provisioned, not that every column provisioned is read. */
    email: str(f.Email) || undefined,
    // Blank stays undefined, which is what isUnclassified looks for.
    kind: (str(f.Kind) as PersonKind) || undefined,
    title: str(f.JobTitle),
    dept: str(f.Department),
    hubLevel: f.HubLevel === null || f.HubLevel === undefined ? null : Number(f.HubLevel),
    loc: toDepotId(f.Depot),
    status: (str(f.Status, 'Active') as PersonStatus) || 'Active',
    signIn: (str(f.SignIn) as SignInState) || undefined,
    mobile: str(f.Mobile),
    start: toIsoDate(f.StartDate),
    mgr: str(f.Manager),
    lic: parseJsonField<string[]>(f.Licences, []),
    groups: parseJsonField<string[]>(f.Groups, []),
    mfa: str(f.MFAMethod),
    notes: str(f.Notes),
    leftOn: toIsoDate(f.LeftOn) || undefined,
    missingSince: toIsoDate(f.MissingSince) || undefined,
    keepUntil: toIsoDate(f.KeepUntil) || undefined,
    onboarded: toIsoDate(f.Onboarded) || undefined,
    offboardedOn: toIsoDate(f.OffboardedOn) || undefined,
    /* str, not toIsoDate: this one keeps its time. SharePoint already returns
       a full ISO timestamp and every other date throws it away, which is right
       for a start date and wrong here — welcoming 95 people over several days,
       "today" is not enough to tell what you have already done this morning. */
    welcomeSentOn: str(f.WelcomeSentOn) || undefined,
    leavingOn: toIsoDate(f.LeavingOn) || undefined,
    lastSignIn: str(f.LastSignIn) || undefined,
    graphId: str(f.GraphId) || undefined,
    lastSyncedAt: str(f.LastSyncedAt) || undefined,
  };
}

/** Notes is the only column Ledger owns; everything else is sync-owned (§10). */
export function personFields(p: Partial<IPerson>): ListItemFields {
  const f: ListItemFields = {};
  if (p.name !== undefined) f.Title = p.name;
  if (p.upn !== undefined) f.UPN = p.upn;
  if (p.email !== undefined) f.Email = p.email;
  /* null, not '', to clear it. SharePoint rejects an empty string for a choice
     column the same way it does for a date, with a 400 that fails the whole
     item rather than the one field. */
  if (p.kind !== undefined) f.Kind = p.kind || null;
  if (p.title !== undefined) f.JobTitle = p.title;
  if (p.dept !== undefined) f.Department = p.dept;
  /* null clears it, the same bargain as a date column: '' on a Number field
     400s the whole item rather than the one value. */
  if (p.hubLevel !== undefined) f.HubLevel = p.hubLevel === null ? null : p.hubLevel;
  if (p.loc !== undefined) f.Depot = fromDepotId(p.loc);
  if (p.status !== undefined) f.Status = p.status;
  if (p.mobile !== undefined) f.Mobile = p.mobile;
  if (p.start !== undefined) f.StartDate = dateOut(p.start);
  if (p.mgr !== undefined) f.Manager = p.mgr;
  if (p.mfa !== undefined) f.MFAMethod = p.mfa;
  if (p.notes !== undefined) f.Notes = p.notes;
  // Hird's. LeftOn and LastSignIn are sync-owned and deliberately absent — the
  // sync is the only thing that witnesses either.
  if (p.keepUntil !== undefined) f.KeepUntil = dateOut(p.keepUntil);
  /* The lifecycle stamps. dateOut, not the raw value: clearing one means null,
     and '' on a date column 400s the whole item rather than the one field —
     which reads as "the save failed" with no clue which column did it. */
  if (p.onboarded !== undefined) f.Onboarded = dateOut(p.onboarded);
  if (p.offboardedOn !== undefined) f.OffboardedOn = dateOut(p.offboardedOn);
  if (p.welcomeSentOn !== undefined) f.WelcomeSentOn = dateOut(p.welcomeSentOn);
  if (p.leavingOn !== undefined) f.LeavingOn = dateOut(p.leavingOn);
  return f;
}

/* ─────────────────────────── Devices ─────────────────────────── */

export function toDevice(id: string, f: ListItemFields): IDevice {
  return {
    id,
    tag: str(f.Title),
    type: (str(f.DeviceType, 'Laptop') as DeviceType) || 'Laptop',
    make: str(f.Make),
    model: str(f.Model),
    serial: str(f.Serial),
    cpu: str(f.CPU),
    ram: str(f.RAM),
    disk: str(f.Storage),
    os: str(f.OS),
    ownership: (str(f.Ownership) as IDevice['ownership']) || undefined,
    hireSupplier: str(f.HireSupplier) || undefined,
    hireRef: str(f.HireRef) || undefined,
    hireEnds: toIsoDate(f.HireEnds) || undefined,
    hireCost:
      f.HireCost === undefined || f.HireCost === null ? undefined : num(f.HireCost),
    bought: toIsoDate(f.PurchaseDate),
    warranty: toIsoDate(f.WarrantyEnd),
    user: lookupId(f.AssignedToLookupId) || null,
    loc: toDepotId(f.Depot),
    ip: str(f.IPAddress),
    mgmtType: (str(f.MgmtType) as IDevice['mgmtType']) || undefined,
    mgmtAddress: str(f.MgmtAddress) || undefined,
    mgmtCredential: str(f.MgmtCredential) || undefined,
    consumables: str(f.Consumables) || undefined,
    printQueue: str(f.PrintQueue) || undefined,
    webUI: str(f.WebUI) || undefined,
    placement: str(f.Placement) || undefined,
    meterReading:
      f.MeterReading === undefined || f.MeterReading === null ? undefined : num(f.MeterReading),
    meterDate: toIsoDate(f.MeterDate) || undefined,
    status: str(f.Status),
    compliance: str(f.Compliance),
    seen: toIsoDate(f.LastSeen),
    enc: str(f.Encryption),
    src: (str(f.RecordSource, 'Manual') as RecordSource) || 'Manual',
    notes: str(f.Notes),
    leftIntune: toIsoDate(f.LeftIntune) || undefined,
    leftNinja: toIsoDate(f.LeftNinja) || undefined,
    hostName: str(f.HostName) || undefined,
    intuneDeviceId: str(f.IntuneDeviceId) || undefined,
    ninjaDeviceId: str(f.NinjaDeviceId) || undefined,
  };
}

/** Only columns Ledger owns. Sync-owned ones are absent deliberately — if a
    field is not here, the browser cannot write it. */
export function deviceFields(d: Partial<IDevice>): ListItemFields {
  const f: ListItemFields = {};
  if (d.tag !== undefined) f.Title = d.tag;
  if (d.type !== undefined) f.DeviceType = d.type;
  if (d.make !== undefined) f.Make = d.make;
  if (d.model !== undefined) f.Model = d.model;
  if (d.serial !== undefined) f.Serial = d.serial;
  if (d.cpu !== undefined) f.CPU = d.cpu;
  if (d.ram !== undefined) f.RAM = d.ram;
  if (d.disk !== undefined) f.Storage = d.disk;
  if (d.ownership !== undefined) f.Ownership = d.ownership || null;
  if (d.hireSupplier !== undefined) f.HireSupplier = d.hireSupplier;
  if (d.hireRef !== undefined) f.HireRef = d.hireRef;
  if (d.hireEnds !== undefined) f.HireEnds = dateOut(d.hireEnds);
  if (d.hireCost !== undefined) f.HireCost = numberOut(d.hireCost);
  if (d.bought !== undefined) f.PurchaseDate = dateOut(d.bought);
  if (d.warranty !== undefined) f.WarrantyEnd = dateOut(d.warranty);
  if (d.loc !== undefined) f.Depot = fromDepotId(d.loc);
  if (d.ip !== undefined) f.IPAddress = d.ip;
  /* null, not '', to clear a choice column. */
  if (d.mgmtType !== undefined) f.MgmtType = d.mgmtType || null;
  if (d.mgmtAddress !== undefined) f.MgmtAddress = d.mgmtAddress;
  if (d.mgmtCredential !== undefined) f.MgmtCredential = d.mgmtCredential;
  if (d.consumables !== undefined) f.Consumables = d.consumables;
  if (d.printQueue !== undefined) f.PrintQueue = d.printQueue;
  if (d.webUI !== undefined) f.WebUI = d.webUI;
  if (d.placement !== undefined) f.Placement = d.placement;
  if (d.meterReading !== undefined) f.MeterReading = numberOut(d.meterReading);
  if (d.meterDate !== undefined) f.MeterDate = dateOut(d.meterDate);
  if (d.status !== undefined) f.Status = d.status;
  if (d.notes !== undefined) f.Notes = d.notes;
  if (d.src !== undefined) f.RecordSource = d.src;
  if (d.user !== undefined) f.AssignedToLookupId = d.user;
  return f;
}

/* ─────────────────────────── Groups ─────────────────────────── */

export function toGroup(id: string, f: ListItemFields): IGroup {
  const members = parseJsonField<IGroupMember[]>(f.Members, []);
  return {
    id,
    name: str(f.Title),
    groupId: str(f.GroupId),
    type: (str(f.GroupType, 'Security') as GroupType) || 'Security',
    desc: str(f.Description),
    email: str(f.Email),
    /* Prefer the stored count. It is written from the same array the members came
       from, so the two only diverge if that array was truncated — and in that
       case the count is the honest number and members.length is not. */
    memberCount: num(f.MemberCount, members.length),
    members,
    owners: parseJsonField<IGroupMember[]>(f.Owners, []),
    rule: str(f.MembershipRule),
    onPrem: bool(f.OnPrem),
    purpose: str(f.Purpose),
    missingSince: toIsoDate(f.MissingSince) || undefined,
    lastSyncedAt: str(f.LastSyncedAt) || undefined,
  };
}

/* Purpose only. Everything else on a group belongs to the nightly sync (§10
   rule 1) — including the member list, which is why there is no way to edit
   membership here: this register records access, it does not grant it. */
export function groupFields(g: Partial<IGroup>): ListItemFields {
  const f: ListItemFields = {};
  if (g.purpose !== undefined) f.Purpose = g.purpose;
  return f;
}

/* ─────────────────────────── Licences ─────────────────────────── */

export function toLicence(id: string, f: ListItemFields): ILicenceSku {
  return {
    // SkuId is the tenant's SKU key and what People.Licences holds; fall back to
    // the item id so a half-populated row still renders.
    id: str(f.SkuId) || id,
    name: str(f.Title),
    short: str(f.ShortName) || str(f.Title),
    total: num(f.PrepaidUnits),
    cost: num(f.MonthlyCost),
    tone: (str(f.Tone, 'info') as Tone) || 'info',
    renews: toIsoDate(f.TermEnds),
    basis: (str(f.Assignment) as 'Per user' | 'Per device') || undefined,
    seatsUsed: f.SeatsUsed === undefined || f.SeatsUsed === null ? undefined : num(f.SeatsUsed),
    hidden: bool(f.Hidden),
  };
}

/* Only the columns Hird owns. Title, SkuId, PrepaidUnits and ConsumedUnits are
   written by the nightly sync (§10 rule 1) and are deliberately absent — this
   is the other half of the same split, and the half Graph cannot supply. */
export function licenceFields(k: Partial<ILicenceSku>): ListItemFields {
  const f: ListItemFields = {};
  if (k.short !== undefined) f.ShortName = k.short;
  if (k.cost !== undefined) f.MonthlyCost = numberOut(k.cost);
  if (k.renews !== undefined) f.TermEnds = dateOut(k.renews);
  if (k.tone !== undefined) f.Tone = k.tone;
  /* null, not '', to clear a choice column — SharePoint answers an empty
     string with a 400 that fails the whole item rather than the one field. */
  if (k.basis !== undefined) f.Assignment = k.basis || null;
  /* null clears a number column; '' is rejected the same way it is for a date. */
  if (k.seatsUsed !== undefined) f.SeatsUsed = numberOut(k.seatsUsed);
  if (k.hidden !== undefined) f.Hidden = k.hidden;
  return f;
}

/* ─────────────────────────── Networks ─────────────────────────── */

/** Subnets as they were stored before DHCP became a tick.
 *
 *  The old column was free text, so it holds "Yes", "10.10.10.100-200", a
 *  server name, or nothing, depending on who typed it. Anything in there was a
 *  claim that the VLAN serves DHCP, and the text itself is very often the scope
 *  — so it becomes the tick plus the range rather than being thrown away.
 *
 *  Runs on read, so no migration has to be scheduled and a row nobody has
 *  opened since is still read correctly. */
function readSubnets(raw: unknown): ISubnet[] {
  const rows = parseJsonField<Record<string, unknown>[]>(raw, []);
  return rows.map((row) => {
    const legacy = typeof row.dhcp === 'string' ? row.dhcp.trim() : '';
    /* "static only" is the common way of writing that a VLAN does not serve
       DHCP, and it is a sentence rather than one of the words for no — so
       matching only no/none/n-a read it as a scope, and the register would have
       shown a management VLAN handing out "static only". */
    const saysNo = /^(no|none|n\/a|-|—)$/i.test(legacy) || /\bstatic|manual|reserved\b/i.test(legacy);
    // Only the word yes — anything else somebody typed is very likely the scope.
    const saysYes = /^(yes|y|true|on|enabled)$/i.test(legacy);
    return {
      vlan: Number(row.vlan ?? 0),
      name: str(row.name),
      cidr: str(row.cidr),
      gw: str(row.gw),
      dhcp: typeof row.dhcp === 'boolean' ? row.dhcp : Boolean(legacy) && !saysNo,
      /* The old text becomes the scope only when it was neither a yes nor a no.
         "Yes" kept as a range would read as a scope somebody had recorded, and
         "static only" kept as one would be worse still. */
      dhcpRange: str(row.dhcpRange) || (saysYes || saysNo ? '' : legacy),
      dhcpServer: str(row.dhcpServer),
      dns: str(row.dns),
      note: str(row.note),
    };
  });
}

export function toNetwork(f: ListItemFields): INetwork {
  return {
    provider: str(f.WANProvider),
    wanType: str(f.WANType),
    speed: str(f.WANSpeed),
    wan: str(f.WANCircuit),
    wanAcct: str(f.WANAccount),
    wanSupport: str(f.WANSupport),
    wanEnds: toIsoDate(f.WANEnds),
    failover: str(f.Failover),
    block: str(f.AddressBlock),
    dns: str(f.DNS),
    subnets: readSubnets(f.SubnetsJson),
    ssids: parseJsonField<ISsid[]>(f.SsidsJson, []),
    vpn: parseJsonField<IVpnTunnel[]>(f.VpnJson, []),
  };
}

/* Subnets, SSIDs and tunnels go back as JSON, matching the read. They are
   nested and never queried independently (§6.1), which is why they are a
   multi-line column rather than three more lists. */
export function networkFields(n: Partial<INetwork>): ListItemFields {
  const f: ListItemFields = {};
  if (n.provider !== undefined) f.WANProvider = n.provider;
  if (n.wan !== undefined) f.WANCircuit = n.wan;
  if (n.wanType !== undefined) f.WANType = n.wanType;
  if (n.speed !== undefined) f.WANSpeed = n.speed;
  if (n.wanAcct !== undefined) f.WANAccount = n.wanAcct;
  if (n.wanSupport !== undefined) f.WANSupport = n.wanSupport;
  if (n.wanEnds !== undefined) f.WANEnds = dateOut(n.wanEnds);
  if (n.failover !== undefined) f.Failover = n.failover;
  if (n.block !== undefined) f.AddressBlock = n.block;
  if (n.dns !== undefined) f.DNS = n.dns;
  if (n.subnets !== undefined) f.SubnetsJson = JSON.stringify(n.subnets);
  if (n.ssids !== undefined) f.SsidsJson = JSON.stringify(n.ssids);
  if (n.vpn !== undefined) f.VpnJson = JSON.stringify(n.vpn);
  return f;
}

/* ─────────────────────────── Locations ─────────────────────────── */

export function toLocation(f: ListItemFields): ILocation {
  return {
    short: str(f.ShortCode),
    addr: str(f.Address),
    postcode: str(f.Postcode),
    phone: str(f.Phone),
    role: str(f.SiteRole),
    contact: lookupId(f.SiteContactLookupId),
    comms: str(f.CommsRoom),
    access: str(f.AccessNotes),
    hours: str(f.Hours),
    notes: str(f.Notes),
  };
}

export function locationFields(l: Partial<ILocation>): ListItemFields {
  const f: ListItemFields = {};
  if (l.short !== undefined) f.ShortCode = l.short;
  if (l.addr !== undefined) f.Address = l.addr;
  if (l.postcode !== undefined) f.Postcode = l.postcode;
  if (l.phone !== undefined) f.Phone = l.phone;
  if (l.role !== undefined) f.SiteRole = l.role;
  if (l.contact !== undefined) f.SiteContactLookupId = l.contact || null;
  if (l.comms !== undefined) f.CommsRoom = l.comms;
  if (l.access !== undefined) f.AccessNotes = l.access;
  if (l.hours !== undefined) f.Hours = l.hours;
  if (l.notes !== undefined) f.Notes = l.notes;
  return f;
}

/* ─────────────────────────── Applications ─────────────────────────── */

export function toApplication(id: string, f: ListItemFields): IApplication {
  return {
    id,
    name: str(f.Title),
    purpose: str(f.Purpose),
    supplier: str(f.Supplier),
    bizOwner: lookupId(f.BusinessOwnerLookupId),
    itOwner: lookupId(f.ITOwnerLookupId),
    users: num(f.UserCount),
    crit: leadingDigit(f.Criticality, 3) as Criticality,
    host: str(f.Hosting),
    sso: str(f.SSO),
    mfa: str(f.MFA),
    data: str(f.DataLocation),
    renews: toIsoDate(f.RenewsOn) || '—',
    cost: num(f.AnnualCost),
    admin: str(f.AdminPortal),
    notes: str(f.Notes),
  };
}

export function applicationFields(a: Partial<IApplication>): ListItemFields {
  const f: ListItemFields = {};
  if (a.name !== undefined) f.Title = a.name;
  if (a.purpose !== undefined) f.Purpose = a.purpose;
  if (a.supplier !== undefined) f.Supplier = a.supplier;
  /* Missing entirely until now, which is why editing an application's
     criticality appeared to do nothing: the save succeeded and simply did not
     carry the one field being changed. */
  if (a.crit !== undefined) f.Criticality = critOut(a.crit);
  if (a.users !== undefined) f.UserCount = numberOut(a.users);
  if (a.host !== undefined) f.Hosting = a.host;
  if (a.sso !== undefined) f.SSO = a.sso;
  if (a.mfa !== undefined) f.MFA = a.mfa;
  if (a.data !== undefined) f.DataLocation = a.data;
  if (a.renews !== undefined) f.RenewsOn = dateOut(a.renews);
  if (a.cost !== undefined) f.AnnualCost = numberOut(a.cost);
  if (a.admin !== undefined) f.AdminPortal = a.admin;
  if (a.notes !== undefined) f.Notes = a.notes;
  if (a.bizOwner !== undefined) f.BusinessOwnerLookupId = a.bizOwner;
  if (a.itOwner !== undefined) f.ITOwnerLookupId = a.itOwner;
  return f;
}

/* ─────────────────────────── Suppliers ─────────────────────────── */

export function toSupplier(id: string, f: ListItemFields): ISupplier {
  const days = num(f.NoticePeriodDays);
  return {
    id,
    name: str(f.Title),
    service: str(f.ServiceProvided),
    acct: str(f.AccountNumber),
    support: str(f.SupportContact),
    portal: str(f.Portal),
    start: toIsoDate(f.ContractStart),
    end: toIsoDate(f.ContractEnd),
    // The expiry aggregation parses this back out to derive the notice deadline,
    // so the "<n> days" shape matters.
    notice: days > 0 ? `${days} days` : 'None',
    cost: num(f.AnnualCost),
    owner: lookupId(f.OwnerLookupId),
    auto: bool(f.AutoRenews),
    notes: str(f.Notes),
  };
}

export function supplierFields(s: Partial<ISupplier>): ListItemFields {
  const f: ListItemFields = {};
  if (s.name !== undefined) f.Title = s.name;
  if (s.service !== undefined) f.ServiceProvided = s.service;
  if (s.acct !== undefined) f.AccountNumber = s.acct;
  if (s.support !== undefined) f.SupportContact = s.support;
  if (s.portal !== undefined) f.Portal = s.portal;
  if (s.start !== undefined) f.ContractStart = dateOut(s.start);
  if (s.end !== undefined) f.ContractEnd = dateOut(s.end);
  if (s.notice !== undefined) f.NoticePeriodDays = parseInt(s.notice, 10) || 0;
  if (s.cost !== undefined) f.AnnualCost = numberOut(s.cost);
  if (s.auto !== undefined) f.AutoRenews = s.auto;
  if (s.notes !== undefined) f.Notes = s.notes;
  if (s.owner !== undefined) f.OwnerLookupId = s.owner;
  return f;
}

/* ─────────────────────────── Domains ─────────────────────────── */

export function toDomain(id: string, f: ListItemFields): IDomain {
  return {
    id,
    name: str(f.Title),
    reg: str(f.Registrar),
    regEnds: toIsoDate(f.RegistrationEnds),
    dns: str(f.DNSProvider),
    sslEnds: toIsoDate(f.CertificateEnds),
    ssl: str(f.CertificateIssuer),
    spf: str(f.SPF),
    dkim: str(f.DKIM),
    dmarc: str(f.DMARC),
    notes: str(f.Notes),
  };
}

/* Only what Hird still owns. The registrar, both dates, the issuer, SPF and
   DMARC belong to the nightly sync now — RDAP, the TLS handshake and DNS all
   report them, and offering any of them in a form would mean typing a value
   that is gone by morning (§10 rule 1).

   DKIM stays, because it needs a selector to look up and there is no way to
   discover one. So does DNSProvider: the sync seeds it from the nameservers on
   creation, which is a guess worth correcting rather than a fact. */
export function domainFields(d: Partial<IDomain>): ListItemFields {
  const f: ListItemFields = {};
  if (d.name !== undefined) f.Title = d.name;
  if (d.dns !== undefined) f.DNSProvider = d.dns;
  if (d.dkim !== undefined) f.DKIM = d.dkim;
  if (d.notes !== undefined) f.Notes = d.notes;
  return f;
}

/* ─────────────────────────── Backup & DR ─────────────────────────── */

export function toBackup(id: string, f: ListItemFields): IBackup {
  return {
    id,
    name: str(f.Title),
    what: str(f.WhatItCovers),
    devices: parseJsonField<string[]>(f.CoversDevices, []),
    apps: parseJsonField<string[]>(f.CoversApps, []),
    product: str(f.Product),
    dest: str(f.Destination),
    alerts: str(f.AlertsTo),
    encrypted: bool(f.Encrypted),
    method: str(f.Method),
    sched: str(f.Schedule),
    ret: str(f.Retention),
    last: toStamp(f.LastRun),
    ok: triBool(f.LastRunOk),
    rpo: str(f.RPO),
    rto: str(f.RTO),
    offsite: bool(f.Offsite),
    immutable: bool(f.Immutable),
    tested: toIsoDate(f.LastTested) || '—',
    testOk: triBool(f.LastTestOk),
    owner: lookupId(f.OwnerLookupId),
    crit: leadingDigit(f.Criticality, 3) as Criticality,
    notes: str(f.Notes),
  };
}

export function backupFields(b: Partial<IBackup>): ListItemFields {
  const f: ListItemFields = {};
  if (b.name !== undefined) f.Title = b.name;
  if (b.devices !== undefined) f.CoversDevices = JSON.stringify(b.devices);
  if (b.apps !== undefined) f.CoversApps = JSON.stringify(b.apps);
  if (b.product !== undefined) f.Product = b.product;
  if (b.dest !== undefined) f.Destination = b.dest;
  if (b.alerts !== undefined) f.AlertsTo = b.alerts;
  if (b.encrypted !== undefined) f.Encrypted = b.encrypted;
  if (b.what !== undefined) f.WhatItCovers = b.what;
  if (b.method !== undefined) f.Method = b.method;
  if (b.sched !== undefined) f.Schedule = b.sched;
  if (b.ret !== undefined) f.Retention = b.ret;
  if (b.rpo !== undefined) f.RPO = b.rpo;
  if (b.rto !== undefined) f.RTO = b.rto;
  if (b.offsite !== undefined) f.Offsite = b.offsite;
  if (b.immutable !== undefined) f.Immutable = b.immutable;
  if (b.tested !== undefined) f.LastTested = dateOut(b.tested);
  if (b.owner !== undefined) f.OwnerLookupId = b.owner;
  if (b.crit !== undefined) f.Criticality = critOut(b.crit);
  if (b.notes !== undefined) f.Notes = b.notes;
  /* LastRun and LastRunOk are pushed by whatever runs the backup, not typed in
     — §11's status-tile route. Writing them by hand would put a green tick
     against a job nobody has confirmed ran. */
  return f;
}

export function recoveryPlanFields(p: Partial<IRecoveryPlan>): ListItemFields {
  const f: ListItemFields = {};
  if (p.name !== undefined) f.Title = p.name;
  if (p.trigger !== undefined) f.Trigger = p.trigger;
  if (p.rto !== undefined) f.TargetRTO = p.rto;
  if (p.last !== undefined) f.LastRehearsed = dateOut(p.last);
  if (p.tier !== undefined) f.Tier = tierOut(p.tier);
  if (p.steps !== undefined) f.StepsJson = JSON.stringify(p.steps);
  return f;
}

export function toRecoveryPlan(id: string, f: ListItemFields): IRecoveryPlan {
  return {
    id,
    name: str(f.Title),
    trigger: str(f.Trigger),
    rto: str(f.TargetRTO),
    last: toIsoDate(f.LastRehearsed) || '—',
    tier: leadingDigit(f.Tier, 1) as IRecoveryPlan['tier'],
    steps: parseJsonField<string[]>(f.StepsJson, []),
  };
}

export function toRestoreTest(f: ListItemFields): IRestoreTest {
  return {
    when: toIsoDate(f.TestedOn),
    what: str(f.Title),
    who: str(f.TestedBy),
    mins: num(f.DurationMinutes),
    ok: bool(f.Succeeded),
    note: str(f.Notes),
  };
}

export function restoreTestFields(t: IRestoreTest): ListItemFields {
  return {
    Title: t.what,
    TestedOn: t.when,
    TestedBy: t.who,
    DurationMinutes: t.mins,
    Succeeded: t.ok,
    Notes: t.note,
  };
}

/* ─────────────────────────── Articles ─────────────────────────── */

export function toArticle(id: string, f: ListItemFields): IArticle {
  return {
    id,
    cat: str(f.Category),
    title: str(f.Title),
    icon: str(f.Icon) || 'ti-file-text',
    desc: str(f.Summary),
    upd: toIsoDate(f.ReviewedOn),
    by: str(f.ReviewedBy),
    steps: parseJsonField<string[]>(f.StepsJson, []),
  };
}

export function articleFields(a: Partial<IArticle>): ListItemFields {
  const f: ListItemFields = {};
  if (a.title !== undefined) f.Title = a.title;
  if (a.cat !== undefined) f.Category = a.cat;
  if (a.icon !== undefined) f.Icon = a.icon;
  if (a.desc !== undefined) f.Summary = a.desc;
  if (a.steps !== undefined) f.StepsJson = JSON.stringify(a.steps);
  if (a.upd !== undefined) f.ReviewedOn = a.upd;
  if (a.by !== undefined) f.ReviewedBy = a.by;
  return f;
}

/* ─────────────────────────── Procedures ─────────────────────────── */

export function toTemplate(id: string, f: ListItemFields): IChecklistTemplate {
  return {
    id,
    name: str(f.Title),
    icon: str(f.Icon) || 'ti-checklist',
    steps: parseJsonField<string[]>(f.StepsJson, []),
  };
}

export function toRun(id: string, f: ListItemFields): IChecklistRun {
  return {
    id,
    tmpl: lookupId(f.TemplateRefLookupId),
    subject: str(f.Title),
    started: toIsoDate(f.StartedOn),
    done: parseJsonField<number[]>(f.CompletedJson, []),
    loc: toDepotId(f.Depot),
  };
}

/* ------------------- Assignments ------------------- */

export function toAssignment(id: string, f: ListItemFields): IAssignment {
  return {
    id,
    device: lookupId(f.DeviceRefLookupId),
    person: lookupId(f.PersonRefLookupId),
    from: toIsoDate(f.FromDate),
    /* undefined, not '', so an open row reads as "still holding it" rather than
       as a spell that ended on no particular day. */
    to: toIsoDate(f.ToDate) || undefined,
    reason: (str(f.Reason) || 'Reassigned') as IAssignment['reason'],
    notes: str(f.Notes),
  };
}

export function assignmentFields(a: Partial<IAssignment>): ListItemFields {
  const f: ListItemFields = {};
  if (a.device !== undefined) f.DeviceRefLookupId = a.device;
  if (a.person !== undefined) f.PersonRefLookupId = a.person;
  if (a.from !== undefined) f.FromDate = dateOut(a.from);
  /* dateOut again, so closing a row and re-opening it both work: '' on a date
     column 400s the whole item, which is how you lose the write that was
     supposed to end somebody's spell with a laptop. */
  if (a.to !== undefined) f.ToDate = dateOut(a.to);
  if (a.reason !== undefined) f.Reason = a.reason || null;
  if (a.notes !== undefined) f.Notes = a.notes;
  return f;
}

/* ─────────────────────────── Credentials ─────────────────────────── */

export function toCredential(id: string, f: ListItemFields): ICredential {
  return {
    id,
    name: str(f.Title),
    system: str(f.System),
    type: str(f.CredentialType),
    owner: lookupId(f.OwnerLookupId),
    vault: str(f.VaultReference),
    rotated: toIsoDate(f.RotatedOn),
    every: num(f.RotationIntervalDays, 365),
    holders: parseJsonField<string[]>(f.Holders, []),
    notes: str(f.Notes),
  };
}

/* ─────────────────────────── Custom records ─────────────────────────── */

/* The index only. There is no value column on Blueprint_Credentials and the
   provisioning script has a guard that fails if one ever appears, so there is
   nothing here that could carry a secret even by accident (§16). */
export function credentialFields(c: Partial<ICredential>): ListItemFields {
  const f: ListItemFields = {};
  if (c.name !== undefined) f.Title = c.name;
  if (c.system !== undefined) f.System = c.system;
  if (c.type !== undefined) f.CredentialType = c.type;
  if (c.vault !== undefined) f.VaultReference = c.vault;
  if (c.rotated !== undefined) f.RotatedOn = dateOut(c.rotated);
  if (c.every !== undefined) f.RotationIntervalDays = c.every;
  if (c.holders !== undefined) f.Holders = JSON.stringify(c.holders);
  if (c.notes !== undefined) f.Notes = c.notes;
  if (c.owner !== undefined) f.OwnerLookupId = c.owner;
  return f;
}

export function toRecordType(id: string, f: ListItemFields): IRecordType {
  return {
    id,
    title: str(f.Title),
    icon: str(f.Icon) || 'ti-file-text',
    description: str(f.Description),
    fields: parseJsonField<IRecordField[]>(f.FieldsJson, []),
    enabled: bool(f.Enabled, true),
    sortOrder: num(f.SortOrder),
  };
}

export function recordTypeFields(t: IRecordType): ListItemFields {
  return {
    Title: t.title,
    Icon: t.icon,
    Description: t.description,
    FieldsJson: JSON.stringify(t.fields),
    Enabled: t.enabled,
    SortOrder: t.sortOrder,
  };
}

export function toCustomRecord(id: string, f: ListItemFields): ICustomRecord {
  const depot = str(f.Depot);
  return {
    id,
    title: str(f.Title),
    typeId: lookupId(f.TypeIdLookupId),
    payload: parseJsonField<Record<string, unknown>>(f.PayloadJson, {}),
    depot: depot ? toDepotId(depot) : undefined,
    expiryDate: toIsoDate(f.ExpiryDate) || undefined,
    status: str(f.Status) || undefined,
    ownerLookup: lookupId(f.OwnerLookupLookupId) || undefined,
  };
}

export function customRecordFields(r: ICustomRecord): ListItemFields {
  const f: ListItemFields = {
    Title: r.title,
    PayloadJson: JSON.stringify(r.payload),
  };
  if (r.typeId) f.TypeIdLookupId = r.typeId;
  if (r.depot) f.Depot = fromDepotId(r.depot as DepotId);
  if (r.expiryDate !== undefined) f.ExpiryDate = dateOut(r.expiryDate);
  if (r.status) f.Status = r.status;
  if (r.ownerLookup) f.OwnerLookupLookupId = r.ownerLookup;
  return f;
}

/* ─────────────────────────── Status tiles ─────────────────────────── */

export function toStatusTile(id: string, f: ListItemFields): IStatusTile {
  const pushed = str(f.PushedAt);
  return {
    id,
    label: str(f.Title),
    val: str(f.TileValue),
    tone: (str(f.Tone, 'muted') as Tone) || 'muted',
    icon: str(f.Icon) || 'ti-activity',
    src: str(f.Source),
    when: pushed ? toStamp(pushed).slice(11) : '—',
  };
}

/** A tile past its TTL is dropped rather than rendered, so a script that stops
    running produces a *missing* tile instead of a permanently green one (§11). */
export function tileIsLive(f: ListItemFields, now = Date.now()): boolean {
  const expires = str(f.ExpiresAt);
  if (!expires) return true;
  const t = new Date(expires).getTime();
  return Number.isNaN(t) ? true : t > now;
}

/* ─────────────────────────── Audit ─────────────────────────── */

/* The icon is presentational and not worth a column — derive it from the action
   so the trail reads consistently however the entry was written. */
const AUDIT_ICONS: [RegExp, string][] = [
  [/reveal/i, 'ti-key'],
  [/rotat/i, 'ti-refresh'],
  [/licen[cs]e/i, 'ti-license'],
  [/device|asset/i, 'ti-device-laptop'],
  [/articl|knowledge/i, 'ti-book-2'],
  [/checklist|step/i, 'ti-checklist'],
  [/view/i, 'ti-eye'],
  [/import/i, 'ti-upload'],
  [/sync/i, 'ti-cloud-download'],
  [/delete|remove/i, 'ti-trash'],
  [/creat|add/i, 'ti-plus'],
  [/updat|edit|save/i, 'ti-pencil'],
];

export function auditIcon(action: string, objectType: string): string {
  for (const [re, icon] of AUDIT_ICONS) {
    if (re.test(action) || re.test(objectType)) return icon;
  }
  return 'ti-history';
}

export function toAuditEntry(f: ListItemFields): IAuditEntry {
  const act = str(f.Title);
  const objectType = str(f.ObjectType);
  return {
    who: str(f.Actor),
    act,
    obj: str(f.ObjectLabel) || str(f.ObjectId),
    when: toStamp(f.OccurredAt),
    icon: auditIcon(act, objectType),
  };
}

export function auditFields(e: Omit<IAuditEntry, 'when'>): ListItemFields {
  return {
    Title: e.act,
    Actor: e.who,
    ObjectLabel: e.obj,
    OccurredAt: new Date().toISOString(),
  };
}

/* ─────────────────────────── Modules & roles ─────────────────────────── */

export function toModule(id: string, f: ListItemFields): IModule {
  return {
    id: str(f.ModuleId) || id,
    label: str(f.Title),
    icon: str(f.Icon) || 'ti-puzzle',
    enabled: bool(f.Enabled, true),
    group: str(f.GroupName),
  };
}

export function toRole(f: ListItemFields): IRoleDefinition {
  return {
    role: (str(f.RoleKey, 'reader') as LedgerRole) || 'reader',
    label: str(f.Title),
    group: str(f.SharePointGroup),
    description: str(f.Description),
    matrix: parseJsonField<Record<string, 'none' | 'read' | 'write'>>(f.MatrixJson, {}),
    special: parseJsonField<ISpecialPermissions>(f.SpecialJson, {
      export: false,
      manageRecordTypes: false,
      manageApiKeys: false,
    }),
  };
}

/* ─────────────────────────── Attachments ─────────────────────────── */

const EXT_KIND: Record<string, FileKind> = {
  pdf: 'pdf',
  doc: 'docx',
  docx: 'docx',
  xls: 'xlsx',
  xlsx: 'xlsx',
  csv: 'xlsx',
  png: 'img',
  jpg: 'img',
  jpeg: 'img',
  gif: 'img',
  webp: 'img',
  txt: 'txt',
  log: 'txt',
  conf: 'txt',
};

function fileKind(name: string): FileKind {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  return EXT_KIND[ext] ?? 'other';
}

function humanSize(bytes: number): string {
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let u = 0;
  while (n >= 1024 && u < units.length - 1) {
    n /= 1024;
    u += 1;
  }
  return `${n < 10 && u > 0 ? n.toFixed(1) : Math.round(n)} ${units[u]}`;
}

export function toAttachment(f: ListItemFields, url?: string): IAttachment {
  const name = str(f.FileLeafRef) || str(f.Title);
  return {
    name,
    kind: fileKind(name),
    size: humanSize(num(f.File_x0020_Size)),
    by: str(f.Editor) || str(f.Author),
    when: toIsoDate(f.Modified),
    ...(url ? { url } : {}),
  };
}
